// Shared pieces of the four rule sets. Each variant file builds a `VariantRules` from these.
import type { Card, CardKey, GameId, GameState, PileRef, Suit } from '../types';
import { rankLow } from '../cards';
import type { Layout } from './mahjong';

export interface VariantRules {
  readonly id: GameId;
  /** Cards per tableau column at the deal (its length = number of columns). */
  readonly columns: readonly number[];
  readonly foundations: number;
  /** FreeCell: free cells (no stock, no waste); 0 in the other games. */
  readonly cells: number;
  readonly decks: 1 | 2;
  /** Suits used to build the deck, in the legacy `deck_colors` order (before the reverse). */
  readonly suits: readonly Suit[];
  /** Spider one/two suits: the suits vary per deal (derived from the pattern); 0 = fixed suits. */
  readonly variableSuits: 0 | 1 | 2;
  /** Cards turned per stock click; `'spider'` deals one card to every column. */
  readonly turn: 1 | 3 | 'spider';
  readonly hasWaste: boolean;
  /** Klondike: the empty stock takes the waste back (free, unlimited). */
  readonly recycle: boolean;
  /** Forty Thieves: every dealt card is face up. */
  readonly allFaceUp: boolean;
  readonly autoMove: boolean;
  /** A foundation card may go back to a column (-15). */
  readonly foundationToTableau: boolean;
  /** Can the run starting at `index` of `pile` be lifted (dragged)? */
  canLift(s: GameState, pile: PileRef, index: number): boolean;
  /** May `run` (bottom first) be dropped on `target`? Assumes the run was liftable. */
  canDrop(s: GameState, run: readonly CardKey[], target: PileRef): boolean;
  /** Double-click / auto-move target of a single card, or null. */
  foundationFor(s: GameState, card: CardKey): number | null;
  /** Does `card` build on `onto` in a column (used for the +5 "tableau to tableau" rule)? */
  buildsOn(card: Card, onto: Card): boolean;
  /** Spider: does the column end in a complete K..A same-suit run (13 cards, face up)? */
  completedRun(s: GameState, column: number): boolean;
  /** Pyramid: a card dropped on a card makes a pair (both leave for the one foundation, the discard pile). */
  readonly pairs?: boolean;
  /** Pyramid: how many times the empty stock takes the waste back (unlimited without it). */
  readonly maxRecycles?: number;
  /** Pyramid: the stock's top card lies face up and is in play; a recycle does not turn a card by itself. */
  readonly stockTopUp?: boolean;
  /** Pyramid: the game is won before every card is home (the pyramid cleared). Without it: all cards home. */
  isWon?(s: GameState): boolean;
  /** Mahjong: the deck is the 144 tiles (cards.ts buildTileDeck), the tableau piles are the places of a layout. */
  readonly tiles?: boolean;
  /** Mahjong: a new deal's ids place by place (a solvable deal on layout `layout`); the deal makes its shuffle
   *  pattern from them. */
  dealIds?(base: readonly string[], rng: () => number, layout: number): string[];
  /** The tile games: the layouts a deal may take (GameState.layout indexes it): Mahjong's shapes (mahjong.ts
   *  MAHJONG_LAYOUTS), Mahjong Connect's two grids (mahjongConnect.ts CONNECT_LAYOUTS). */
  readonly layouts?: readonly Layout[];
  /** The tile games: every pair that may leave now (place indexes): Mahjong's free matching tiles, Connect's matching
   *  tiles a line can join. The hint takes the first; none = the "No moves" window. */
  pairsLeft?(s: GameState): Array<[number, number]>;
  /** The tile games: the tiles still on the board shuffled over their places, solvable when it can be (the free
   *  shuffle when no move is left, owner 2026-10-24). */
  reshuffle?(s: GameState, rng: () => number): Array<readonly CardKey[]>;
  /** Kierki, Makao, 3-5-8, Tysiąc: a card game at a table, played by its own module (hearts/, makao/, 358/, 1000/);
   *  the engine only holds the game id. The value names the module. Kulki (kulki/) too: not a card game, but played
   *  by its own module the same way. */
  readonly table?: 'hearts' | 'makao' | '358' | '1000' | 'kulki';
  /** A logic game, not a card game (Kulki): the Games window lists it under "Inne gry logiczne" (owner 2026-11-03). */
  readonly logic?: true;
  /** FreeCell: may auto-move start (every card can go home)? Without it: no stock, no waste, no face-down card. */
  autoFinishReady?(s: GameState): boolean;
}

export const card = (s: GameState, key: CardKey): Card => s.deck[key]!;
export const topOf = (pile: readonly CardKey[]): CardKey | undefined => pile[pile.length - 1];

export function pileCards(s: GameState, ref: PileRef): readonly CardKey[] {
  switch (ref.kind) {
    case 'stock':
      return s.piles.stock;
    case 'waste':
      return s.piles.waste;
    case 'foundation':
      return s.piles.foundations[ref.index] ?? [];
    case 'tableau':
      return s.piles.tableau[ref.index] ?? [];
    case 'cell':
      return s.piles.cells[ref.index] ?? [];
  }
}

/** Standard foundation rule: an Ace on an empty slot, then up by suit (A=1 .. K=13). */
export function fitsFoundation(s: GameState, key: CardKey, foundation: number): boolean {
  const pile = s.piles.foundations[foundation];
  if (!pile) return false;
  const c = card(s, key);
  const top = topOf(pile);
  if (top === undefined) return c.rank === 'A';
  const t = card(s, top);
  return t.suit === c.suit && rankLow(c.rank) === rankLow(t.rank) + 1;
}

/**
 * Double-click target: an Ace goes to the LEFTMOST empty slot (always; fixes KL-F06/FIX-010), any
 * other card to the first foundation (left to right) whose top accepts it.
 */
export function standardFoundationFor(s: GameState, key: CardKey): number | null {
  for (let i = 0; i < s.piles.foundations.length; i++) if (fitsFoundation(s, key, i)) return i;
  return null;
}

/** Top card of a non-empty pile only (waste, foundation). */
export function liftTopOnly(s: GameState, pile: PileRef, index: number): boolean {
  const cards = pileCards(s, pile);
  return cards.length > 0 && index === cards.length - 1;
}

/** A single card dropped on a foundation; runs never go there. */
export function dropOnFoundation(s: GameState, run: readonly CardKey[], foundation: number): boolean {
  return run.length === 1 && fitsFoundation(s, run[0]!, foundation);
}
